import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/20/solid";
import UserAvatar from "./UserAvatar";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const UserManagerCard = () => {
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState({});
  const [scope, setScope] = useState("activeBlocked");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [usersResponse, activityResponse] = await Promise.all([
        api.get(`/users/panel-users?gradeId=3&scope=${scope}`),
        api.get("/users/activity-summary?days=7"),
      ]);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setActivity(activityResponse.data || {});
    } catch (err) {
      console.error("Erreur chargement utilisateurs :", err);
      setError("Impossible de charger les utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) =>
      [user.Surnom, user.Email, user.Grade?.Nom, user.Etat?.Nom]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [search, users]);

  const changeEtat = async (user, newEtat) => {
    await api.put("/users/change-etat", {
      userId: user.UtilisateurID,
      newEtat,
    });
    await loadUsers();
  };

  return (
    <section className="mx-auto my-8 max-w-5xl rounded-2xl border border-sky-500/10 bg-white/85 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70">
      <div className="border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
        <p className="text-sm font-bold uppercase text-sky-500 dark:text-sky-400">Utilisateurs</p>
        <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">Gestion des comptes</h2>
      </div>

      <div className="space-y-4 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un utilisateur"
            className="w-full rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white sm:max-w-sm"
          />
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value)}
            className="rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
          >
            <option value="activeBlocked">Actifs et bloqués</option>
            <option value="deleted">Supprimés</option>
            <option value="all">Tous</option>
          </select>
        </div>

        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}

        {loading ? (
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Chargement...</p>
        ) : filteredUsers.length === 0 ? (
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Aucun utilisateur.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredUsers.map((user) => {
              const userActivity = activity[String(user.UtilisateurID)];
              return (
                <article key={user.UtilisateurID} className="rounded-xl border border-sky-500/10 bg-white/80 p-4 shadow-sm dark:bg-slate-950/60">
                  <div className="flex items-start gap-4">
                    <UserAvatar user={user} className="size-14 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-lg font-black text-slate-950 dark:text-white">{user.Surnom}</h3>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">{user.Email}</p>
                      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {user.Etat?.Nom || `État #${user.EtatID}`} - Dernière connexion : {formatDateTime(user.LastLogin)}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Logs 7j : {userActivity?.totalLogsLastNDays || 0}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    {user.EtatID === 3 ? (
                      <button
                        type="button"
                        onClick={() => changeEtat(user, 1)}
                        className="inline-flex items-center gap-2 rounded-lg border border-emerald-300/40 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-200"
                      >
                        <LockOpenIcon className="size-4" />
                        Débloquer
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => changeEtat(user, 3)}
                        className="inline-flex items-center gap-2 rounded-lg border border-amber-300/40 px-3 py-2 text-xs font-bold text-amber-700 hover:bg-amber-500/10 dark:text-amber-200"
                      >
                        <LockClosedIcon className="size-4" />
                        Bloquer
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default UserManagerCard;
